import { supabase } from '../lib/supabase';

export const DEFAULT_ROOM_TYPES = [
    ['Lecture & Theory',['Lecture Hall','Standard Classroom','Seminar Room','Exam Coordinator Room']],
    ['Laboratories (Science & ICT)',['Computer Laboratory','Science Laboratory','Chemistry Laboratory','Physics Laboratory','Biology Laboratory','Medical Laboratory','Networking Lab','Hardware Maintenance Lab']],
    ['Engineering Workshops',['Workshop','Technical Drawing','Automotive Engineering Workshop','Electrical Engineering Workshop','Mechanical Engineering Workshop','Welding & Fabrication Workshop','Masonry & Carpentry Workshop','Plumbing Workshop','Electronics Workshop']],
    ['Vocational & Specialized',['Hospitality Training Kitchen','Fashion & Design Workshop','Agriculture Demonstration Farm','Hairdressing & Beauty Therapy Salon','Food & Beverage Service Area','Field']],
    ['Administrative & Staff',['Staff Office','HOD Office','Dean Office','Admin Office','Boardroom','Conference Room','Library','Store']]
].flatMap(([category,names], categoryIndex) => names.map((name,index) => ({ id:`fallback-${categoryIndex}-${index}`, name, category, sort_order:categoryIndex*100+index, is_active:true })));

const toUi = (room, departmentMap = {}) => ({ ...room, departmentId: room.department_id, departmentName: room.department?.name || departmentMap[room.department_id]?.name || 'Not assigned', type: room.room_type, status: room.status ? room.status.charAt(0).toUpperCase() + room.status.slice(1) : 'Active' });
const toDb = room => ({ id: room.id, name: room.name, department_id: room.departmentId || null, room_type: room.type, capacity: Number(room.capacity), status: String(room.status || 'active').toLowerCase(), location: room.location || null, updated_at: new Date().toISOString() });

export const roomService = {
    getRoomTypes: async () => {
        const { data, error } = await supabase.from('room_types').select('id,code,name,category,requires_specialized_space,is_active,sort_order').eq('is_active', true).order('sort_order');
        if (error) {
            console.warn('Database room-type catalogue unavailable; using built-in catalogue:', error.message);
            return DEFAULT_ROOM_TYPES;
        }
        const databaseNames = new Set((data || []).map(type => type.name));
        return [...(data || []), ...DEFAULT_ROOM_TYPES.filter(type => !databaseNames.has(type.name))].sort((a,b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    },
    createRoomType: async (data) => {
        const { data: result, error } = await supabase.from('room_types').insert(data).select().single();
        if (error) throw error;
        return { success: true, roomType: result };
    },
    updateRoomType: async (id, data) => {
        const { error } = await supabase.from('room_types').update(data).eq('id', id);
        if (error) throw error;
        return { success: true };
    },
    deleteRoomType: async (id) => {
        const { error } = await supabase.from('room_types').delete().eq('id', id);
        if (error) throw error;
        return { success: true };
    },
    getAll: async (departmentId = null) => {
        // Load rooms without an embedded relationship so a stale/missing schema-cache
        // relationship cannot hide the entire room list.
        const { data: scopedRows, error } = await supabase.rpc('get_scoped_rooms');
        if (error) throw error;
        let data = (scopedRows || []).map(row => row.room || row).sort((a,b) => String(a.name || '').localeCompare(String(b.name || '')));
        if (departmentId && departmentId !== 'all') data = data.filter(room => String(room.department_id) === String(departmentId));
        const departmentIds = [...new Set((data || []).map(room => room.department_id).filter(Boolean))];
        let departmentMap = {};
        if (departmentIds.length) {
            const { data: departments, error: departmentError } = await supabase.from('departments').select('id,name,code').in('id', departmentIds);
            if (departmentError) console.warn('Room departments could not be loaded:', departmentError.message);
            departmentMap = (departments || []).reduce((map, department) => ({ ...map, [department.id]: department }), {});
        }
        return (data || []).map(room => toUi(room, departmentMap));
    },
    getById: async id => {
        const { data: scopedRows, error } = await supabase.rpc('get_scoped_rooms');
        if (error) throw error;
        const data = (scopedRows || []).map(row => row.room || row).find(room => String(room.id) === String(id));
        if (!data) throw new Error('Room is outside your organisational scope.');
        return toUi(data);
    },
    generateRoomId: async departmentCode => {
        const prefix = `${String(departmentCode).replace(/[^a-zA-Z0-9]/g, '').toUpperCase()}-`;
        const { data, error } = await supabase.from('rooms').select('id').ilike('id', `${prefix}%`);
        if (error) throw error;
        const max = (data || []).reduce((highest, room) => Math.max(highest, Number(String(room.id).split('-').pop()) || 0), 0);
        return `${prefix}${String(max + 1).padStart(3, '0')}`;
    },
    create: async roomData => {
        const { data, error } = await supabase.from('rooms').insert(toDb(roomData)).select().single();
        if (error) throw error;
        return { success: true, room: toUi(data) };
    },
    batchCreate: async rooms => {
        const results = [];
        for (const room of rooms) {
            try { results.push(await roomService.create(room)); } catch (error) { results.push({ success: false, error: error.message, room }); }
        }
        return { success: results.every(result => result.success), results };
    },
    update: async (id, roomData) => {
        const payload = toDb({ ...roomData, id }); delete payload.id;
        const { error } = await supabase.from('rooms').update(payload).eq('id', id);
        if (error) throw error;
        return { success: true };
    },
    delete: async id => {
        const { error } = await supabase.from('rooms').delete().eq('id', id);
        if (error) throw error;
        return { success: true };
    },
    getCompatibleRooms: async unit => {
        const { data: requirements, error } = await supabase.rpc('get_unit_delivery_requirements', { target_unit: String(unit.id) });
        if (error) throw error;
        const requirement = requirements?.[0] || { required_room_type: unit.roomType, minimum_capacity: unit.enrolled || 0 };
        const { data: rooms, error: roomError } = await supabase.from('rooms').select('*').eq('status', 'active').eq('room_type', requirement.required_room_type).gte('capacity', requirement.minimum_capacity || 0).order('capacity');
        if (roomError) throw roomError;
        return { requirement, rooms: rooms || [] };
    }
};
